import React, { type ComponentProps, type FunctionComponent, type ReactNode } from 'react';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import da from 'date-fns/locale/da';
import en from 'date-fns/locale/en-GB';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { useTranslation } from 'common';
import BasicTextField from '../../input/BasicTextField/BasicTextField';
import { useFSLDatePickerStyles } from './FSLDatePicker.styles';

interface IFSLDatePickerProps {
  label: string;
  date: Date | null | undefined;
  onDateChanged: (d: Date | undefined) => void;
  error?: boolean;
  helperText?: ReactNode;
  disabled?: boolean;
  // The new `DatePicker` in MUI 5 uses a different prop type than `KeyboardDatePickerProps`.
  // So you can define something like this if needed:
  datepickerProps?: Omit<ComponentProps<typeof DatePicker>, 'label' | 'value' | 'onChange' | 'renderInput'>;
}

const FSLDatePicker: FunctionComponent<IFSLDatePickerProps> = ({ label, date, onDateChanged, error, helperText, datepickerProps, disabled }): JSX.Element => {
  const { translateDate, language } = useTranslation();
  const classes = useFSLDatePickerStyles();

  // Switch between English or Danish locale:
  const locale = language === 'en' ? en : da;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <DatePicker
        disabled={disabled}
        views={['year', 'month', 'day']}
        className={classes.root}
        value={date}
        label={label}
        onChange={(newValue) => {
          onDateChanged(newValue instanceof Date ? newValue : undefined);
        }}
        // Spread other props
        {...datepickerProps}
        // MUI v5 pickers rely on a renderInput prop to render the underlying TextField
        renderInput={(params) => (
          <BasicTextField
            {...params}
            value={date ? translateDate(date) : ''}
            error={error}
            helperText={helperText}
            isReadOnly
            InputLabelProps={{
              shrink: true,
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default FSLDatePicker;
